import { useCallback, useState } from 'react';
import { SavedFilter } from '../types';
import { FilterContext, getFilters, useLogging } from '@ecosystem/newron-client-lib';

export enum ErrorType {
  FAILED_TO_LOAD = 'FAILED_TO_LOAD',
}

export type SavedFiltersReturnType = {
  /** saved filters context */
  filterContext: FilterContext;
  /** saved filters, undefined until fetch is called */
  savedFilters: SavedFilter[];
  /** function to trigger loading of saved filters */
  fetch: () => Promise<void>;
  /** if filters are loading */
  loading: boolean;
  /** if there was an error */
  error?: ErrorType;
};

export const useSavedFilters = (filterContext: FilterContext) => {
  const { error: logError } = useLogging();

  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorType>();

  const loadFilters = useCallback(async () => {
    setLoading(true);
    try {
      const savedFilters = await getFilters<SavedFilter>(filterContext);
      setSavedFilters(savedFilters);
      setError(null);
    } catch (err) {
      logError(err);
      setError(ErrorType.FAILED_TO_LOAD);
    } finally {
      setLoading(false);
    }
  }, [logError, filterContext]);

  return {
    filterContext,
    fetch: loadFilters,
    savedFilters,
    loading,
    error,
  };
};
